import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Button } from '@mui/material';
// components
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useOrganizations } from 'src/@nicheaim/fhir-react';
import { Organization } from 'src/@nicheaim/fhir-base/mappings/Organization';
import fhirSystem from 'src/fhir/system';

// ----------------------------------------------------------------------

type FormValue = {
  name: string;
};

type LOBFormProps = {
  isEdit?: boolean;
  currentLOB?: Organization;
};

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
});

const resolver = yupResolver(schema);

export default function NewLOBForm({ isEdit, currentLOB }: LOBFormProps) {
  const navigate = useNavigate();
  const { plan: healthPlan = '' } = useParams();
  const [, { create, update }] = useOrganizations({ autofetch: false });

  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () =>
      ({
        name: currentLOB?.name || '',
      } as FormValue),
    [currentLOB]
  );

  const methods = useForm({ resolver, defaultValues });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isEdit && currentLOB) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentLOB]);

  const onSubmit = async (data: FormValue) => {
    try {
      if (isEdit && currentLOB) {
        await update({
          ...currentLOB,
          id: currentLOB.id,
          resourceType: 'Organization',
          ...data,
        });
      } else {
        const [lob] = await create({
          resourceType: 'Organization',
          identifier: [
            {
              system: fhirSystem.lineOfBusiness.asString(),
            },
            {
              system: fhirSystem.lineOfBusiness.withId(healthPlan).asString(),
            },
          ],
          partOf: {
            type: 'Organization',
            id: healthPlan,
            reference: `Organization/${healthPlan}`,
          },
          name: data.name,
          active: true,
        });
        lob.identifier![0].value = lob.id;
        lob.identifier![1].value = lob.id;
        await update(lob);
      }

      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      navigate(isEdit ? '../..' : '..');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 3 }}>
        <Stack
          gap={3}
          sx={{
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
        >
          <RHFTextField name="name" label="LOB Name" />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }} gap={2}>
          <Button
            variant="text"
            color="inherit"
            component={RouterLink}
            to={isEdit ? '../..' : '..'}
          >
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {!isEdit ? 'Create LOB' : 'Save Changes'}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  );
}
